import { z } from 'zod';

// Briques communes des contrats (`api.md` § Conventions, T06 § common.ts).

export const Id = z.string().min(1);
export type Id = z.infer<typeof Id>;

/** Date ISO 8601 avec fuseau (`2026-09-01T12:00:00.000Z`), telle que produite par `toISOString()`. */
export const IsoDate = z.iso.datetime({ offset: true });
export type IsoDate = z.infer<typeof IsoDate>;

export const PAGE_SIZE_DEFAULT = 50;
export const PAGE_SIZE_MAX = 200;

/** `?page=1&pageSize=50` (max 200) ; les valeurs arrivent en chaînes depuis la query string. */
export const PaginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});
export type PaginationQuery = z.infer<typeof PaginationQuery>;

export const SortOrder = z.enum(['asc', 'desc']);
export type SortOrder = z.infer<typeof SortOrder>;

/** `?sort=field&order=asc|desc`, champs autorisés listés par route. */
export const sortQuery = <const T extends readonly [string, ...string[]]>(fields: T) =>
  z.object({
    sort: z.enum(fields).optional(),
    order: SortOrder.default('desc'),
  });

/** `{ items, page, pageSize, total }`. */
export const paginated = <T extends z.ZodType>(item: T) =>
  z.object({
    items: z.array(item),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
  });
export type Paginated<T> = { items: T[]; page: number; pageSize: number; total: number };

/**
 * Paramètre de query répété (`?type=url&type=site`) ou unique (`?type=url`) : le validateur Hono
 * livre une chaîne quand la clé n'apparaît qu'une fois, un tableau sinon.
 */
export const queryArray = <T extends z.ZodType>(item: T) =>
  z.preprocess((value) => (typeof value === 'string' ? [value] : value), z.array(item));

/** Booléen de query string : `true` / `false` (et `1` / `0`) ; `z.coerce.boolean` accepterait `'false'` comme vrai. */
export const BooleanQuery = z.preprocess((value) => {
  if (value === 'true' || value === '1') return true;
  if (value === 'false' || value === '0') return false;
  return value;
}, z.boolean());
